/**
 * OddsTable — Shared odds display component
 * Extracted from: displayOddsTable() lines 2185-2429
 * Used by: EventOddsWidget, HistoricalOddsWidget, OddsComparisonWidget
 *
 * Renders a game's bookmaker odds organized by market type,
 * with best/average columns, LAY odds support, and team indicators.
 */

import { formatOddsValue, formatBookmakerName, formatMarketName } from '../../lib/oddsFormatters'
import { EXCHANGE_BOOKMAKERS, getBookmakerLogoPath } from '../../lib/oddsConstants'
import TeamLogo from './TeamLogo'
import './OddsTable.css'

/**
 * Process raw game data into structured market data with LAY support
 * Mirrors the original displayOddsTable logic (lines 2209-2283)
 */
function processGameMarkets(game) {
  if (!game.bookmakers || game.bookmakers.length === 0) return {}

  const marketData = {}
  const layOddsStore = {}

  game.bookmakers.forEach(bookmaker => {
    bookmaker.markets.forEach(market => {
      const isLayMarket = market.key.includes('_lay')
      const baseMarketKey = isLayMarket ? market.key.replace('_lay', '') : market.key

      if (!marketData[baseMarketKey]) {
        marketData[baseMarketKey] = { bookmakers: {}, outcomes: {} }
      }
      if (!layOddsStore[baseMarketKey]) {
        layOddsStore[baseMarketKey] = {}
      }

      market.outcomes.forEach(outcome => {
        if (isLayMarket && baseMarketKey === 'h2h') {
          const bookmakerKey = `${bookmaker.key}_LAY`
          if (!layOddsStore[baseMarketKey][outcome.name]) {
            layOddsStore[baseMarketKey][outcome.name] = {}
          }
          layOddsStore[baseMarketKey][outcome.name][bookmakerKey] = {
            price: outcome.price, point: outcome.point,
            description: outcome.description, isLay: true,
          }
          marketData[baseMarketKey].bookmakers[bookmakerKey] = bookmaker.title
        } else {
          if (!marketData[baseMarketKey].outcomes[outcome.name]) {
            marketData[baseMarketKey].outcomes[outcome.name] = {}
          }
          marketData[baseMarketKey].outcomes[outcome.name][bookmaker.key] = {
            price: outcome.price, point: outcome.point,
            description: outcome.description, isLay: false,
          }
          marketData[baseMarketKey].bookmakers[bookmaker.key] = bookmaker.title
        }
      })
    })
  })

  // Merge LAY odds into same team rows
  Object.keys(layOddsStore).forEach(marketKey => {
    Object.keys(layOddsStore[marketKey]).forEach(teamName => {
      if (!marketData[marketKey].outcomes[teamName]) {
        marketData[marketKey].outcomes[teamName] = {}
      }
      Object.keys(layOddsStore[marketKey][teamName]).forEach(bookKey => {
        marketData[marketKey].outcomes[teamName][bookKey] = layOddsStore[marketKey][teamName][bookKey]
      })
    })
  })

  return marketData
}

function OddsTable({ games, oddsFormat = 'american' }) {
  if (!games || games.length === 0) {
    return <div className="ot-empty">No games with odds available</div>
  }

  return (
    <div className="ot-container">
      {games.map((game, gi) => {
        const marketData = processGameMarkets(game)
        if (Object.keys(marketData).length === 0) return null

        const startTime = new Date(game.commence_time)
        const timeStr = `${startTime.toLocaleDateString()} ${startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`

        return (
          <div key={game.id || gi} className="ot-game">
            <div className="ot-game-header">
              <span className="ot-game-title">
                {game.sport_title || 'Game'} — <TeamLogo teamName={game.away_team} sportKey={game.sport_key} size={18} />{game.away_team} @ <TeamLogo teamName={game.home_team} sportKey={game.sport_key} size={18} />{game.home_team}
              </span>
              <span className="ot-game-time">{timeStr}</span>
            </div>

            {Object.keys(marketData).map(marketKey => {
              const market = marketData[marketKey]
              const bookmakerKeys = Object.keys(market.bookmakers)

              // Build outcome order: away, home, then others
              const outcomeOrder = []
              if (game.away_team) outcomeOrder.push(game.away_team)
              if (game.home_team) outcomeOrder.push(game.home_team)
              Object.keys(market.outcomes).forEach(o => {
                if (!outcomeOrder.includes(o)) outcomeOrder.push(o)
              })

              return (
                <div key={marketKey} className="ot-market">
                  <div className="ot-table-wrap">
                    <table className="ot-table">
                      <thead>
                        <tr>
                          <th className="ot-th-team">
                            {formatMarketName(marketKey)}
                            <span className="ot-book-count">{bookmakerKeys.length} books</span>
                          </th>
                          <th className="ot-th-best">Best</th>
                          <th className="ot-th-avg">Avg</th>
                          {bookmakerKeys.map(key => {
                            const isLay = key.includes('_LAY')
                            const baseKey = key.replace('_LAY', '')
                            const isExchange = EXCHANGE_BOOKMAKERS.includes(baseKey)
                            return (
                              <th key={key} className="ot-th-book">
                                {getBookmakerLogoPath(baseKey) && (
                                  <img
                                    className="ot-book-logo"
                                    src={getBookmakerLogoPath(baseKey)}
                                    alt=""
                                    width={16}
                                    height={16}
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                )}
                                {formatBookmakerName(baseKey)}
                                {isExchange && <span className="ot-exchange-icon" title="Exchange bookmaker">{'\u{1F4CA}'}</span>}
                                {isLay && <span className="ot-lay-badge">LAY</span>}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {outcomeOrder.map(outcomeName => {
                          const outcomeData = market.outcomes[outcomeName]
                          if (!outcomeData) return null

                          const prices = Object.values(outcomeData)
                            .map(o => o.price)
                            .filter(p => p !== undefined)
                          if (prices.length === 0) return null

                          const bestPrice = Math.max(...prices)
                          const avgPrice = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
                          const firstOutcome = Object.values(outcomeData)[0]

                          return (
                            <tr key={outcomeName}>
                              <td className="ot-team-cell">
                                {outcomeName === game.home_team && (
                                  <span className="ot-indicator ot-home" />
                                )}
                                {outcomeName === game.away_team && (
                                  <span className="ot-indicator ot-away" />
                                )}
                                <TeamLogo teamName={outcomeName} sportKey={game.sport_key} size={18} />
                                <strong>{outcomeName}</strong>
                                {firstOutcome.description && (
                                  <span className="ot-desc">{firstOutcome.description}</span>
                                )}
                              </td>
                              <td className={`ot-odds ot-best ${bestPrice > 0 ? 'ot-pos' : 'ot-neg'}`}>
                                {formatOddsValue(bestPrice, marketKey, firstOutcome.point, oddsFormat)}
                              </td>
                              <td className="ot-odds ot-avg">
                                {formatOddsValue(avgPrice, marketKey, firstOutcome.point, oddsFormat)}
                              </td>
                              {bookmakerKeys.map(bookKey => {
                                const bookOdds = outcomeData[bookKey]
                                if (!bookOdds) return <td key={bookKey} className="ot-odds ot-na">-</td>

                                const isBest = bookOdds.price === bestPrice
                                return (
                                  <td
                                    key={bookKey}
                                    className={`ot-odds ${bookOdds.price > 0 ? 'ot-pos' : 'ot-neg'} ${isBest ? 'ot-best' : ''} ${bookOdds.isLay ? 'ot-lay' : ''}`}
                                  >
                                    {formatOddsValue(bookOdds.price, marketKey, bookOdds.point, oddsFormat)}
                                  </td>
                                )
                              })}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default OddsTable
